import { InterviewLinkValue } from './InterviewLinkValue';
import { Link } from 'react-router-dom';
import { CopyIdButton, type PanelSpan } from '@/components/shared';
import {
  formatDateTime,
  formatProductLine,
  formatVoiceProvider,
  formatEnabledState,
  formatInterviewLimit,
  formatLanguages,
  formatOwnerDisplayName,
  formatParticipantRecordingPrivacyMode,
  formatShortId,
  formatStatus,
} from '../format';
import type { BackofficeProjectDetail } from '../types';
import { ProjectDetailPanel } from './ProjectDetailPanelGrid';

export interface ProjectDetailsPanelProps {
  project: BackofficeProjectDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function DetailRow({
  label,
  value,
  copyValue,
  copyLabel,
  href,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copyLabel?: string;
  href?: string;
}) {
  const content = href ? (
    <Link
      to={href}
      className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
    >
      {value}
    </Link>
  ) : (
    <span className="min-w-0 truncate">{value}</span>
  );

  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-neutral-900">{label}</dt>
      <dd className="min-w-0 text-neutral-700">
        {copyValue && copyLabel ? (
          <div className="group inline-flex min-w-0 items-center gap-2">
            {content}
            <CopyIdButton value={copyValue} label={copyLabel} />
          </div>
        ) : (
          content
        )}
      </dd>
    </div>
  );
}

export function ProjectDetailsPanel({ project, panelId, currentSpan }: ProjectDetailsPanelProps) {
  const ownerDisplayName = formatOwnerDisplayName(project);
  const detailItems = [
    {
      id: 'id',
      label: 'ID',
      content: (
        <DetailRow
          key="id"
          label="ID"
          value={formatShortId(project.id)}
          copyValue={project.id}
          copyLabel="Copy study ID"
        />
      ),
    },
    {
      id: 'title',
      label: 'Title',
      content: (
        <DetailRow
          key="title"
          label="Title"
          value={project.title}
          copyValue={project.title}
          copyLabel="Copy title"
        />
      ),
    },
    {
      id: 'owner',
      label: 'Owner',
      content: (
        <DetailRow
          key="owner"
          label="Owner"
          value={ownerDisplayName}
          {...(project.ownerUserId
            ? {
                href: `/users/${project.ownerUserId}`,
                copyValue: project.ownerUserId,
                copyLabel: 'Copy owner ID',
              }
            : {})}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      content: <DetailRow key="status" label="Status" value={formatStatus(project.status)} />,
    },
    {
      id: 'interview-link',
      label: 'Interview link',
      content: (
        <div
          key="interview-link"
          className="grid gap-1 border-b border-neutral-100 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4"
        >
          <dt className="font-semibold text-neutral-900">Interview link</dt>
          <dd className="min-w-0 text-neutral-700">
            <InterviewLinkValue projectId={project.id} />
          </dd>
        </div>
      ),
    },
    {
      id: 'organization',
      label: 'Organization',
      content: (
        <DetailRow
          key="organization"
          label="Organization"
          value={project.organizationName}
          href={`/organizations/${project.organizationId}`}
        />
      ),
    },
    {
      id: 'product-line',
      label: 'Product line',
      content: (
        <DetailRow
          key="product-line"
          label="Product line"
          value={formatProductLine(project.productLine)}
        />
      ),
    },
    {
      id: 'voice-assistant',
      label: 'Voice assistant',
      content: (
        <DetailRow
          key="voice-assistant"
          label="Voice assistant"
          value={formatVoiceProvider(project.voiceProvider)}
        />
      ),
    },
    {
      id: 'shared-with',
      label: 'Shared with',
      content: (
        <DetailRow
          key="shared-with"
          label="Shared with"
          value={`${project.sharedUserCount} ${project.sharedUserCount === 1 ? 'person' : 'people'}`}
        />
      ),
    },
    {
      id: 'languages',
      label: 'Languages',
      content: (
        <DetailRow key="languages" label="Languages" value={formatLanguages(project.languages)} />
      ),
    },
    {
      id: 'interview-limit',
      label: 'Interview limit',
      content: (
        <DetailRow
          key="interview-limit"
          label="Interview limit"
          value={formatInterviewLimit(project.interviewLimit)}
        />
      ),
    },
    {
      id: 'interview-recording-privacy',
      label: 'Interview recording privacy',
      content: (
        <DetailRow
          key="interview-recording-privacy"
          label="Interview recording privacy"
          value={formatParticipantRecordingPrivacyMode(project.participantRecordingPrivacyMode)}
        />
      ),
    },
    {
      id: 'collect-participant-email',
      label: 'Collect participant email',
      content: (
        <DetailRow
          key="collect-participant-email"
          label="Collect participant email"
          value={formatEnabledState(project.collectParticipantEmail)}
        />
      ),
    },
    {
      id: 'skip-participant-landing-page',
      label: 'Skip participant landing page',
      content: (
        <DetailRow
          key="skip-participant-landing-page"
          label="Skip participant landing page"
          value={
            typeof project.skipParticipantLandingPage === 'boolean'
              ? formatEnabledState(project.skipParticipantLandingPage)
              : 'Not available'
          }
        />
      ),
    },
    {
      id: 'created',
      label: 'Created',
      content: (
        <DetailRow key="created" label="Created" value={formatDateTime(project.createdAt)} />
      ),
    },
    {
      id: 'launched',
      label: 'Launched',
      content: (
        <DetailRow key="launched" label="Launched" value={formatDateTime(project.launchedAt)} />
      ),
    },
    {
      id: 'updated',
      label: 'Updated',
      content: (
        <DetailRow key="updated" label="Updated" value={formatDateTime(project.updatedAt)} />
      ),
    },
  ];

  return (
    <ProjectDetailPanel
      title="Study details"
      span="2x2"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
    />
  );
}
